/**
 * Course versioning: the learner's What's New notice as data, and the text it
 * reads as. One notice in three parts, in reading order: the new lessons, the
 * updated lessons with the parts that changed in each, then the actions this
 * learner has to complete.
 *
 * The notice is per learner — only lessons they'd already started count as
 * "updated" for them, and new lessons can be required for one learner and
 * optional for another — so admins never edit it. The Publish Update modal
 * renders the same text as a read-only example.
 */

export type LessonPart = 'content' | 'caseStudies' | 'quiz' | 'quizQuestions' | 'liveScenario' | 'feedbackQuestions';
export type PartChange = 'added' | 'edited' | 'removed';
export type Requirement = 'required' | 'optional';

export interface LessonPartChange {
  part: LessonPart;
  change: PartChange;
  /** How many questions (quizQuestions / feedbackQuestions); 1 when absent. */
  count?: number;
  /** This learner has to complete (or redo) this part. */
  required?: boolean;
}

export interface WhatsNewLesson {
  taskId: string;
  title: string;
  requirement: Requirement;
  /** What changed in an updated lesson; empty for a new lesson. */
  parts: LessonPartChange[];
}

export interface LearnerWhatsNew {
  newLessons: WhatsNewLesson[];
  updatedLessons: WhatsNewLesson[];
}

/** The slice of the learner's record the fallback below reads (GET programs/{grpId}/updates). */
export interface LearnerUpdateRecord {
  badges: Record<string, string>;
  pendingActions: Array<{ taskId: string; kind: string; optional: boolean }>;
}

/** What each pending-action kind asks the learner to redo in an updated lesson. */
const PENDING_PART: Record<string, LessonPart> = {
  reread: 'content',
  quiz: 'quiz',
  scenario: 'liveScenario'
};

/**
 * The notice for a BE build that doesn't return `learnerWhatsNew`: rebuilt from
 * the learner's own `badges` and `pendingActions`, in course order. Required
 * parts come from the pending-action kinds; what changed in an Optional update
 * isn't known, so it's listed without its parts. Lessons without a title are
 * skipped.
 */
export function deriveLearnerWhatsNew(
  record: LearnerUpdateRecord,
  /** The program's lessons, id → title, in course order. */
  lessonTitles: Map<string, string>
): LearnerWhatsNew {
  const pending = new Map<string, LearnerUpdateRecord['pendingActions']>();
  (record.pendingActions || []).forEach((p) => pending.set(p.taskId, [...(pending.get(p.taskId) || []), p]));
  const ids = new Set([...Object.keys(record.badges || {}), ...Array.from(pending.keys())]);

  const newLessons: WhatsNewLesson[] = [];
  const updatedLessons: WhatsNewLesson[] = [];
  lessonTitles.forEach((title, taskId) => {
    if (!ids.has(taskId) || !title) return;
    const actions = pending.get(taskId) || [];
    const requirement: Requirement = actions.some((a) => !a.optional) ? 'required' : 'optional';
    if (record.badges?.[taskId] === 'new' || actions.some((a) => a.kind === 'new')) {
      newLessons.push({ taskId, title, requirement, parts: [] });
      return;
    }
    const parts: LessonPartChange[] = actions
      .filter((a) => PENDING_PART[a.kind])
      .map((a) => ({ part: PENDING_PART[a.kind], change: 'edited' as const, required: !a.optional }));
    updatedLessons.push({ taskId, title, requirement, parts });
  });
  return { newLessons, updatedLessons };
}

export interface WhatsNewText {
  newParagraph: string | null;
  updatedParagraph: string | null;
  /** "Lesson (New): complete in full", one per lesson the learner has to act on. */
  actions: string[];
}

const quote = (title: string) => `“${title}”`;
const REQUIREMENT_LABEL: Record<Requirement, string> = { required: 'Required', optional: 'Optional' };

/** "a", "a and b", "a, b and c". */
export const joinAnd = (items: string[]): string =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

const questions = (n: number, adjective: string, noun: string): string =>
  `${n} ${adjective ? `${adjective} ` : ''}${noun}${n === 1 ? '' : 's'}`;

/** What changed, as a phrase: "2 new quiz questions", "an updated live scenario". */
export function describePart(p: LessonPartChange): string {
  const n = p.count ?? 1;
  switch (p.part) {
    case 'content':
      return p.change === 'removed' ? 'Lesson Body removed' : 'updated Lesson Body';
    case 'caseStudies':
      return p.change === 'added'
        ? 'new case studies'
        : p.change === 'edited'
        ? 'updated case studies'
        : 'case studies removed';
    case 'quiz':
      return p.change === 'added' ? 'a new quiz' : p.change === 'edited' ? 'an updated quiz' : 'the quiz removed';
    case 'quizQuestions':
      return p.change === 'removed'
        ? `${questions(n, '', 'quiz question')} removed`
        : questions(n, p.change === 'added' ? 'new' : 'edited', 'quiz question');
    case 'liveScenario':
      return p.change === 'added'
        ? 'a new Roleplay'
        : p.change === 'edited'
        ? 'an updated Roleplay'
        : 'the Roleplay removed';
    case 'feedbackQuestions':
      return p.change === 'removed'
        ? `${questions(n, '', 'Survey question')} removed`
        : questions(n, p.change === 'added' ? 'new' : 'edited', 'Survey question');
    default:
      return '';
  }
}

/** What the learner has to do about a required part: "complete the 2 new quiz questions". */
function partAction(p: LessonPartChange): string | null {
  if (p.change === 'removed') return null;
  if (p.part === 'content') return 're-read the updated Lesson Body';
  // "a new quiz" → "the new quiz", "1 new quiz question" → "the new quiz question"; larger counts stay.
  return `complete the ${describePart(p).replace(/^(an?|1) /, '')}`;
}

export function lessonAction(lesson: WhatsNewLesson, kind: 'new' | 'updated'): string | null {
  if (kind === 'new') return lesson.requirement === 'required' ? 'complete in full' : null;
  const steps = lesson.parts
    .filter((p) => p.required)
    .map(partAction)
    .filter((s): s is string => Boolean(s));
  return steps.length ? joinAnd(steps) : null;
}

export function whatsNewText({ newLessons, updatedLessons }: LearnerWhatsNew): WhatsNewText {
  let newParagraph: string | null = null;
  if (newLessons.length) {
    const list = joinAnd(newLessons.map((l) => `${quote(l.title)} (${REQUIREMENT_LABEL[l.requirement]})`));
    newParagraph =
      newLessons.length === 1
        ? `1 new lesson has been added: ${list}. It’s badged ‘New’ in your lessons menu.`
        : `${newLessons.length} new lessons have been added: ${list}. Each is badged ‘New’ in your lessons menu.`;
  }

  let updatedParagraph: string | null = null;
  if (updatedLessons.length) {
    const entries = updatedLessons.map((l) => {
      const what = joinAnd(l.parts.map(describePart).filter(Boolean));
      return `${quote(l.title)} (${REQUIREMENT_LABEL[l.requirement]})${what ? `: ${what}` : ''}`;
    });
    const hasParts = updatedLessons.some((l) => l.parts.length > 0);
    const list = entries.join(hasParts ? '; ' : ', ');
    const partsNote = hasParts ? ', and the parts that changed are badged ‘New’ or ‘Updated’ inside the lesson' : '';
    updatedParagraph =
      updatedLessons.length === 1
        ? `1 lesson has been updated: ${list}. It’s badged ‘Updated’ in your lessons menu${partsNote}.`
        : `${updatedLessons.length} lessons have been updated: ${list}. Each is badged ‘Updated’ in your lessons menu${partsNote}.`;
  }

  const actions: string[] = [];
  newLessons.forEach((l) => {
    const a = lessonAction(l, 'new');
    if (a) actions.push(`${l.title} (New): ${a}`);
  });
  updatedLessons.forEach((l) => {
    const a = lessonAction(l, 'updated');
    if (a) actions.push(`${l.title} (Updated): ${a}`);
  });

  return { newParagraph, updatedParagraph, actions };
}
